import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadProject } from "@/services/projectService";
import { getShotList } from "@/services/shotService";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Download, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import SplashLoader from "@/components/SplashLoader";

export default function ShotListView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [shotList, setShotList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectData, shotListData] = await Promise.all([
        loadProject(projectId),
        getShotList(projectId)
      ]);
      setProject(projectData);
      setShotList(shotListData);
    } catch (error) {
      console.error("Error fetching shot list:", error);
      toast.error("Failed to load shot list");
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Shot #', 'Scene', 'Shot Type', 'Camera Angle', 'Camera Movement', 'Lens', 'Duration', 'Lighting'];
    const rows = shotList.map(shot => [
      shot.shot_number,
      shot.scene_number,
      shot.shot_type,
      shot.camera_angle,
      shot.camera_movement,
      shot.lens_recommendation,
      shot.duration_estimate,
      shot.lighting_notes
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'project'}_shot_list.csv`;
    a.click();
    toast.success("Shot list exported");
  };

  if (loading) {
    return <SplashLoader message="Loading shots..." />;
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-[#fafafa]">
      {/* Header */}
      <div className="border-b border-[#27272a] bg-[#121212] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/project/${projectId}`)}
            className="text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#27272a]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Shot List - {project?.name}</h1>
            <p className="text-xs text-[#71717a]">{shotList.length} shots</p>
          </div>
        </div>
        <Button
          data-testid="export-csv-btn"
          onClick={exportToCSV}
          className="bg-[#f97316] hover:bg-[#ea580c] text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Shot List Table */}
      <ScrollArea className="h-[calc(100vh-80px)]">
        <div className="max-w-7xl mx-auto p-6">
          {shotList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Clapperboard className="w-16 h-16 text-[#3f3f46] mb-4" />
              <h3 className="text-lg font-medium text-[#a1a1aa] mb-2">No shots generated yet</h3>
              <p className="text-sm text-[#71717a]">Generate a storyboard first to see the shot list</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(
                shotList.reduce((acc, shot) => {
                  if (!acc[shot.scene_number]) acc[shot.scene_number] = [];
                  acc[shot.scene_number].push(shot);
                  return acc;
                }, {})
              ).map(([sceneNum, shots]) => (
                <div key={sceneNum}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-[#27272a]" />
                    <h2 className="text-sm font-mono text-[#3b82f6] tracking-wide">SCENE {sceneNum}</h2>
                    <div className="h-px flex-1 bg-[#27272a]" />
                  </div>
                  <div className="space-y-3">
                    {shots.map((shot) => (
                      <Card
                        key={shot.shot_number}
                        data-testid={`shot-card-${shot.shot_number}`}
                        className="bg-[#18181b] border-[#3f3f46] p-4"
                      >
                        <div className="grid grid-cols-12 gap-4">
                          <div className="col-span-1">
                            <div className="text-xs text-[#71717a]">Shot</div>
                            <div className="font-mono text-[#f97316] font-medium">{shot.shot_number}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-[#71717a]">Type</div>
                            <div className="text-sm font-medium">{shot.shot_type}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-[#71717a]">Camera Angle</div>
                            <div className="text-sm">{shot.camera_angle}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-[#71717a]">Movement</div>
                            <div className="text-sm">{shot.camera_movement}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-[#71717a]">Lens</div>
                            <div className="text-sm">{shot.lens_recommendation}</div>
                          </div>
                          <div className="col-span-1">
                            <div className="text-xs text-[#71717a]">Duration</div>
                            <div className="text-sm">{shot.duration_estimate}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-[#71717a]">Lighting</div>
                            <div className="text-sm text-[#a1a1aa] truncate">{shot.lighting_notes}</div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-[#27272a]">
                          <div className="text-xs text-[#71717a] mb-1">Description</div>
                          <p className="text-sm text-[#a1a1aa]">{shot.description}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}